// @flow
import React, { Component, Fragment } from 'react';
import BrandHead from 'components/brand/BrandHead';
import BrandContent from 'components/brand/BrandContent';
import BrandTags from 'components/brand/BrandTags';
import { BaseActions, BrandsActions, CommonActions, DisplayActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { BrandData, TocItem } from 'store/modules/brands';
import BrandToc from 'components/brand/BrandToc';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import ScrollToTop from 'components/common/ScrollToTop';
import BrandPlaceholder from 'components/brand/BrandPlaceholder';
import throttle from 'lodash/throttle';
import BrandLeftSticker from 'components/brand/BrandLeftSticker/BrandLeftSticker';
import BrandSeriesInfo from 'components/brand/BrandSeriesInfo/BrandSeriesInfo';

type Props = {
  username: ?string,
  urlSlug: ?string,
  brand: ?BrandData,
  toc: ?(TocItem[]),
  activeHeading: ?string,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class BrandViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    BrandsActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    const { username, urlSlug, shouldCancel } = this.props;
    if (!username || !urlSlug) return;
    try {
      if (shouldCancel) return;
      await BrandsActions.readBrand({
        username,
        urlSlug,
      });
      if (!this.props.brand) return;
      const { id } = this.props.brand;
      BrandsActions.getSequences(id);
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      BrandsActions.unloadBrand();
      this.initialize();
    }
  }

  componentWillUnmount() {
    BrandsActions.unloadBrand();
  }

  onSetToc = (toc: ?(TocItem[])) => {
    BrandsActions.setToc(toc);
  };

  onToggleLike = () => {
    const { brand, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!brand) return;
    if (brand.liked) {
      BrandsActions.unlike(brand.id);
    } else {
      BrandsActions.like(brand.id);
    }
  };

  onToggleAskRemove = () => {
    BrandsActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { brand, history, routerHistory } = this.props;
    BrandsActions.toggleAskRemove();
    if (!brand) return;
    try {
      await CommonActions.removeBrand(brand.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    this.initialize();
    const { hash } = this.props.location;
    if (hash !== '') {
      BrandsActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  render() {
    const { brand, toc, activeHeading, username, currentUsername, askRemove, logged } = this.props;
    const { onSetToc, onActivateHeading } = this;

    if (!brand) return <BrandPlaceholder />;
    if (!brand.user) return <BrandPlaceholder />;
    const plainTextBody = convertToPlainText(brand.body);

    const url = `${process.env.BASE_URL}/@${brand.user.username}/brands/${brand.url_slug}`;
    const baseTitle = `${brand.title} | 캐시바이`;
    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="description" content={plainTextBody} />
          <link rel="canonical" href={url} />
          <meta property="og:url" content={url} />
          <meta property="og:type" content="article" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={plainTextBody} />
          {brand.thumbnail && <meta property="og:image" content={brand.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={plainTextBody} />
          {brand.thumbnail && <meta name="twitter:image" content={brand.thumbnail} />}
        </Helmet>
        <BrandLeftSticker
          logged={logged}
          likes={brand.likes}
          liked={brand.liked}
          onToggleLike={this.onToggleLike}
          url={this.props.match.url}
          title={brand.title}
          username={brand.user.username}
          informCopy={this.informCopy}
          disabled={!logged}
        />
        <BrandToc
          toc={toc}
          activeHeading={activeHeading}
          onActivateHeading={this.onActivateHeading}
        />
        <BrandHead
          id={brand.id}
          date={brand.released_at}
          title={brand.title}
          tags={brand.tags}
          categories={brand.categories}
          user={brand.user}
          likes={brand.likes}
          liked={brand.liked}
          onToggleLike={this.onToggleLike}
          ownBrand={currentUsername === username}
          onAskRemove={this.onToggleAskRemove}
          logged={logged}
          isPrivate={brand.is_private}
          url={this.props.match.url}
          informCopy={this.informCopy}
        />
        {brand.series && <BrandSeriesInfo series={brand.series} username={username || ''} />}
        <BrandContent
          thumbnail={brand.thumbnail}
          body={brand.body}
          onSetToc={onSetToc}
          onActivateHeading={onActivateHeading}
          theme={brand.meta.code_theme}
        />
        <BrandTags tags={brand.tags} />
        <QuestionModal
          open={askRemove}
          title="브랜드 삭제"
          description="이 브랜드를 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ brands, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    brand: brands.brand,
    toc: brands.toc,
    activeHeading: brands.activeHeading,
    likeInProcess: pender.pending['brands/LIKE'] || pender.pending['brands/UNLIKE'],
    askRemove: brands.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
  }),
  () => ({}),
)(withRouter(BrandViewer));



// WEBPACK FOOTER //
// ./src/containers/brand/BrandViewer.js